/**
 * Career Explorer — the v1 flow, rebuilt on the v2 registry.
 *
 * Search and filter the canonical corpus (server-side, from
 * `career_intelligence_content`), then read a rich card for every role and a
 * quick-look panel that shows the full brief without leaving the list:
 * growth, demand 2026–2040, salary bands, employers, entry route, skills.
 *
 * The card facts come from the read-only Career Intelligence shards, loaded
 * lazily as each card scrolls into view. Nothing here is scored, nothing is
 * written, and a fact we don't have is simply not shown.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  browseCareerContent,
  loadFacets,
  CAREER_SORT_OPTIONS,
  type CareerSortKey,
  type BrowsePage,
  type CareerContentSummary,
} from "@/lib/careerContent";
import {
  loadCareerIntel,
  demandLevel,
  demandTone,
  forecastSentence,
  type CareerIntel,
} from "@/lib/careerIntel";
import {
  loadCareerSheetRow,
  remoteLabel,
  balanceLabel,
  salaryBandLpa,
  type CareerSheetRow,
} from "@/lib/careerSheet";
import { fetchSavedCareers, saveCareer, unsaveCareer } from "@/lib/savedCareers";
import { resolveInitialSort, writeSortPref } from "@/lib/careerSortPref";
import FamilyClusterHelp from "@/components/career/FamilyClusterHelp";
import InfoPopout from "@/components/InfoPopout";
import EmployerLink from "@/components/career/EmployerLink";
import DemandBreakdown from "@/components/career/DemandBreakdown";
import PinToVisionBoard from "@/components/career/PinToVisionBoard";

import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/shell/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  ArrowRight,
  ArrowUpDown,
  BarChart3,
  Briefcase,
  Building2,
  Compass,
  Globe,
  GraduationCap,
  Heart,
  IndianRupee,
  Clock,
  Loader2,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  TrendingUp,
  Users,
  Landmark,
} from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";

import { toast } from "@/hooks/use-toast";

import DayInLifeTimeline from "@/components/career/DayInLifeTimeline";
import { getDayInLife, hasDayInLife } from "@/data/dayInLife";
import OccupationStandardsNote from "@/components/OccupationStandardsNote";
import OnetAttribution from "@/components/OnetAttribution";
import { classificationBasis } from "@/lib/occupationStandards";

/** Sources store these scores on 0–10 or 0–100. Always read out on a /10 scale. */
function outOfTen(value: number): string {
  const v = value > 10 ? value / 10 : value;
  const r = Math.round(v * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

const PAGE_SIZE = 30;

export default function CareersExplorer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  // Honours the OS "reduce motion" setting: entrances and chart transitions
  // become instant rather than absent, so nothing is lost, only stilled.
  const reduceMotion = useReducedMotion() ?? false;


  const [searchInput, setSearchInput] = useState(searchParams.get("q") ?? "");
  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  const [family, setFamily] = useState(searchParams.get("family") ?? "all");
  const [cluster, setCluster] = useState(searchParams.get("cluster") ?? "all");
  const [level, setLevel] = useState(searchParams.get("level") ?? "all");
  // URL wins so a shared link opens the list it promised; otherwise we honour
  // the order this learner last chose.
  const [sort, setSort] = useState<CareerSortKey>(() =>
    resolveInitialSort(searchParams.get("sort"), "demand_desc"),
  );

  // Remember it for next time — including "Most in demand".
  useEffect(() => {
    writeSortPref(sort);
  }, [sort]);

  const [facets, setFacets] = useState<{ families: string[]; clusters: string[]; levels: string[] }>({
    families: [],
    clusters: [],
    levels: [],
  });

  const [page, setPage] = useState<BrowsePage>({ rows: [], total: 0 });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [selected, setSelected] = useState<CareerContentSummary | null>(null);
  const [saved, setSaved] = useState<Set<string>>(new Set());

  // load facets once
  useEffect(() => {
    void loadFacets().then(setFacets);
  }, []);

  // Hydrate pins from the learner's account. Runs on sign-in change and again
  // whenever they come back to the tab, so a heart tapped on a phone shows up
  // here without a reload.
  const hydrateSaved = useCallback(async () => {
    const rows = await fetchSavedCareers(user?.id ?? null);
    setSaved(new Set(rows.map((r) => r.ciId)));
  }, [user?.id]);

  useEffect(() => {
    void hydrateSaved();
    const onFocus = () => {
      if (document.visibilityState === "visible") void hydrateSaved();
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [hydrateSaved]);

  // Optimistic, but honest: if the write fails the heart goes back where it was
  // and we say so, rather than pretending the role is kept.
  const toggleSave = useCallback(
    (c: CareerContentSummary) => {
      const wasSaved = saved.has(c.ci_id);
      setSaved((prev) => {
        const next = new Set(prev);
        if (wasSaved) next.delete(c.ci_id);
        else next.add(c.ci_id);
        return next;
      });

      const write = wasSaved
        ? unsaveCareer(user?.id ?? null, c.ci_id)
        : saveCareer(user?.id ?? null, {
            ciId: c.ci_id,
            title: c.canonical_name,
            family: c.career_family,
          });

      void write.catch(() => {
        setSaved((prev) => {
          const next = new Set(prev);
          if (wasSaved) next.add(c.ci_id);
          else next.delete(c.ci_id);
          return next;
        });
        toast({
          title: "That didn't save",
          description: "Your list is safe — try the heart again in a moment.",
        });
      });
    },
    [saved, user?.id]
  );


  // debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // sync filters -> URL
  useEffect(() => {
    const next: Record<string, string> = {};
    if (search) next.q = search;
    if (family !== "all") next.family = family;
    if (cluster !== "all") next.cluster = cluster;
    if (level !== "all") next.level = level;
    if (sort !== "demand_desc") next.sort = sort;
    setSearchParams(next, { replace: true });
  }, [search, family, cluster, level, sort, setSearchParams]);

  // reset on filter change
  useEffect(() => {
    setOffset(0);
    setLoading(true);
    browseCareerContent({
      q: search,
      family: family === "all" ? undefined : family,
      cluster: cluster === "all" ? undefined : cluster,
      level: level === "all" ? undefined : level,
      sort,
      limit: PAGE_SIZE,
      offset: 0,
    })
      .then((p) => setPage(p))
      .finally(() => setLoading(false));
  }, [search, family, cluster, level, sort]);

  const loadMore = async () => {
    setLoadingMore(true);
    const nextOffset = offset + PAGE_SIZE;
    const p = await browseCareerContent({
      q: search,
      family: family === "all" ? undefined : family,
      cluster: cluster === "all" ? undefined : cluster,
      level: level === "all" ? undefined : level,
      sort,
      limit: PAGE_SIZE,
      offset: nextOffset,
    });
    setPage((prev) => ({ rows: [...prev.rows, ...p.rows], total: p.total }));
    setOffset(nextOffset);
    setLoadingMore(false);
  };

  const hasMore = useMemo(() => page.rows.length < page.total, [page]);

  const activeFilters =
    (family !== "all" ? 1 : 0) + (cluster !== "all" ? 1 : 0) + (level !== "all" ? 1 : 0);

  const clearAll = () => {
    setFamily("all");
    setCluster("all");
    setLevel("all");
    setSort("demand_desc");
    setSearchInput("");
  };

  return (
    <AppShell title="Careers">
      <div className="space-y-5">
        <header className="calm-card p-6">
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Career Explorer
          </p>
          <h1 className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl">
            Find the work that fits you.
          </h1>
          <p className="mt-2 max-w-prose text-sm leading-relaxed text-muted-foreground">
            {page.total.toLocaleString()} careers you can search by name, family, cluster or level.
            Tap any role for the quick look — what you'd be doing, what it pays, how demand moves to
            2040, and how you get in.
          </p>
        </header>

        {/* Search + filters */}
        <section className="calm-card-quiet space-y-5 p-5 sm:p-6">
          <div className="relative">
            <Search
              className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Try “designer”, “nurse”, “data”…"
              aria-label="Search careers"
              className="h-12 rounded-2xl border-border/60 bg-[hsl(var(--surface-2))] pl-10 text-base"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Select value={family} onValueChange={setFamily}>
              <SelectTrigger aria-label="Filter by career family" className="h-11 rounded-2xl border-border/60 bg-transparent">
                <SelectValue placeholder="Family" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All families</SelectItem>
                {facets.families.map((f) => (
                  <SelectItem key={f} value={f}>
                    {f}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={cluster} onValueChange={setCluster}>
              <SelectTrigger aria-label="Filter by career cluster" className="h-11 rounded-2xl border-border/60 bg-transparent">
                <SelectValue placeholder="Cluster" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All clusters</SelectItem>
                {facets.clusters.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger aria-label="Filter by career level" className="h-11 rounded-2xl border-border/60 bg-transparent">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All levels</SelectItem>
                {facets.levels.map((l) => (
                  <SelectItem key={l} value={l}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <label htmlFor="career-sort" className="text-xs font-medium text-muted-foreground">
                Sort by
              </label>
              <Select value={sort} onValueChange={(v) => setSort(v as CareerSortKey)}>
                <SelectTrigger
                  id="career-sort"
                  aria-label="Sort careers"
                  className="h-11 w-full rounded-2xl border-border/60 bg-transparent sm:w-56"
                >
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  {CAREER_SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="ml-auto flex items-center gap-1">
              <FamilyClusterHelp label="Family or cluster?" />
              <InfoPopout label="How these are ranked" title="How this list works">
                <p>
                  <span className="font-medium text-foreground">Order.</span> Strongest market
                  demand first, then projected growth. Roles we have no demand evidence for sit in
                  the middle — never at the bottom, because that would read as a verdict we can't
                  back up.
                </p>
                <div className="space-y-2 border-t border-border/50 pt-3">
                  <OccupationStandardsNote />
                  <p>
                    Each listing shows the basis applied to it — its ISCO-08 major group — or says
                    the classification is pending when we hold no code for it.
                  </p>
                </div>
                <div className="border-t border-border/50 pt-3">
                  <OnetAttribution />
                </div>
              </InfoPopout>
            </div>
          </div>

          {(activeFilters > 0 || search) && (
            <div className="flex items-center justify-between gap-3 border-t border-border/50 pt-4">
              <p className="text-xs text-muted-foreground">
                {page.total.toLocaleString()} match{page.total === 1 ? "" : "es"}
              </p>
              <button
                type="button"
                onClick={clearAll}
                className="min-h-11 rounded-full px-4 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
              >
                Clear all
              </button>
            </div>
          )}
        </section>


        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2">

            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="calm-card-quiet h-[172px] animate-pulse p-5" aria-hidden="true">
                <div className="h-3 w-24 rounded-full bg-muted/50" />
                <div className="mt-3 h-4 w-40 rounded-full bg-muted/60" />
                <div className="mt-4 h-6 w-28 rounded-full bg-muted/40" />
              </div>
            ))}
            <p className="sr-only" role="status">
              Loading careers
            </p>
          </div>
        ) : page.rows.length === 0 ? (
          <section className="calm-card p-8 text-center">
            <p className="font-display text-lg font-semibold">Nothing matched that.</p>
            <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
              Try a shorter word, or clear a filter — there are thousands of careers waiting.
            </p>
            <button
              type="button"
              onClick={clearAll}
              className="mt-5 inline-flex min-h-12 items-center rounded-2xl bg-primary px-5 text-sm font-medium text-primary-foreground transition-transform hover:scale-[1.02] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Start over
            </button>
          </section>
        ) : (
          <ul
            className="grid gap-3 sm:grid-cols-2"
            aria-label="Career results"
          >

            {page.rows.map((c, i) => (
              <motion.li
                key={c.ci_id}
                initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : { duration: 0.32, delay: Math.min(i % PAGE_SIZE, 8) * 0.03 }
                }
              >
                <CareerCard
                  c={c}
                  saved={saved.has(c.ci_id)}
                  onToggleSave={toggleSave}
                  onOpen={setSelected}
                />
              </motion.li>
            ))}
            <li className="sr-only" role="status">
              {page.rows.length} of {page.total.toLocaleString()} careers shown
            </li>
          </ul>

        )}

        {hasMore && !loading && (
          <div className="flex justify-center pt-1">
            <Button
              onClick={loadMore}
              disabled={loadingMore}
              variant="outline"
              className="min-h-12 rounded-2xl px-6"
            >
              {loadingMore ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                `Show more · ${page.rows.length} of ${page.total.toLocaleString()}`
              )}
            </Button>
          </div>
        )}
      </div>

      <QuickLook career={selected} onClose={() => setSelected(null)} />
    </AppShell>
  );
}

/* ------------------------------------------------------------------ */
/* Card                                                                */
/* ------------------------------------------------------------------ */

/**
 * Loads the intelligence for one role — plus the spreadsheet's numeric
 * lifestyle signals — but only once the card is on screen.
 */
function useVisibleIntel(name: string) {
  const ref = useRef<HTMLElement | null>(null);
  const [intel, setIntel] = useState<CareerIntel | null>(null);
  const [sheet, setSheet] = useState<CareerSheetRow | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    const fetchIt = () => {
      void loadCareerIntel(name).then((i) => {
        if (!cancelled) setIntel(i);
      });
      void loadCareerSheetRow(name).then((s) => {
        if (!cancelled) setSheet(s);
      });
    };
    if (typeof IntersectionObserver === "undefined") {
      fetchIt();
      return () => {
        cancelled = true;
      };
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          fetchIt();
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [name]);

  return { ref, intel, sheet };
}


/**
 * One role. Memoised on (row, saved) so scrolling, searching or pinning one
 * card does not re-render the other twenty-nine — the list stays smooth on a
 * low-end phone. The parent passes stable callbacks and the card supplies its
 * own row, which is what keeps the memo honest.
 */
const CareerCard = memo(function CareerCard({
  c,
  saved,
  onToggleSave,
  onOpen,
}: {
  c: CareerContentSummary;
  saved: boolean;
  onToggleSave: (c: CareerContentSummary) => void;
  onOpen: (c: CareerContentSummary) => void;
}) {
  const { ref, intel, sheet } = useVisibleIntel(c.canonical_name);
  const handleOpen = useCallback(() => onOpen(c), [onOpen, c]);
  const handleToggleSave = useCallback(() => onToggleSave(c), [onToggleSave, c]);
  const basis = useMemo(() => classificationBasis(c), [c]);
  const { growth, demand, remote, balance, satisfaction, salary } = useMemo(
    () => ({
      growth: intel?.growthPct ?? sheet?.growthPct ?? null,
      demand: intel?.demand["2026"] ?? sheet?.demand2026 ?? null,
      // The spreadsheet's numeric signals read shorter than the shard's prose,
      // so the card stays one comfortable height. Prose lives in the quick look.
      remote: remoteLabel(sheet?.remoteScore) ?? intel?.remote ?? null,
      balance: sheet?.workLifeBalance ?? intel?.workLifeBalance ?? null,
      satisfaction: sheet?.jobSatisfaction ?? intel?.jobSatisfaction ?? null,
      salary: intel?.salaryStart ?? salaryBandLpa(sheet?.salaryMinInr, sheet?.salaryMaxInr),
    }),
    [intel, sheet],
  );


  return (
    <article
      ref={ref as React.RefObject<HTMLElement>}
      className="calm-card group flex h-full flex-col p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[0_18px_40px_-24px_hsl(var(--primary)/0.55)] motion-reduce:transform-none motion-reduce:transition-none"
    >
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={handleOpen}
          className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-xl"
          aria-label={`${c.canonical_name}${c.career_cluster ? `, in ${c.career_cluster}` : ""} — quick look`}
        >
          {c.career_cluster && (
            <span className="block truncate text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              {c.career_cluster}
            </span>
          )}
          <h2 className="mt-1.5 line-clamp-2 font-display text-base font-bold leading-snug text-foreground">
            {c.canonical_name}
          </h2>
        </button>
        <button
          type="button"
          onClick={handleToggleSave}
          aria-pressed={saved}
          aria-label={saved ? `Remove ${c.canonical_name} from saved` : `Save ${c.canonical_name}`}
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-colors ${
            saved
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-border/60 text-muted-foreground hover:text-foreground"
          }`}
        >
          <Heart className={`h-4 w-4 ${saved ? "fill-current" : ""}`} aria-hidden="true" />
        </button>
      </div>

      {intel?.brief && (
        <p className="mt-2.5 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
          {intel.brief}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5">
        {growth != null && (
          <Chip tone="grow">
            <TrendingUp className="h-3 w-3" aria-hidden="true" /> {Math.round(growth)}% growth
          </Chip>
        )}
        {demand && <Chip tone="demand">{demand} demand · 2026</Chip>}
        {remote && (
          <Chip>
            <Globe className="h-3 w-3" aria-hidden="true" /> {remote}
          </Chip>
        )}
        {c.level && <Chip tone="level">{c.level}</Chip>}
        <span
          title={basis.description}
          aria-label={basis.description}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${
            basis.resolved
              ? "border-border/60 bg-muted/30 text-muted-foreground"
              : "border-dashed border-border/60 text-muted-foreground/80"
          }`}
        >
          <Landmark className="h-3 w-3" aria-hidden="true" /> {basis.label}
        </span>
        {hasDayInLife(c.canonical_name) && (
          <Chip tone="day">
            <Clock className="h-3 w-3" aria-hidden="true" /> Day in the life
          </Chip>
        )}
      </div>

      <div className="mt-2">
        <DemandBreakdown ciId={c.ci_id} name={c.canonical_name} />
      </div>

      {intel?.employers?.length ? (
        <p className="mt-3 flex items-start gap-1.5 border-t border-border/40 pt-2.5 text-[11px] text-muted-foreground">
          <Building2 className="mt-0.5 h-3 w-3 shrink-0 text-primary/70" aria-hidden="true" />
          <span className="line-clamp-1">
            <span className="font-semibold text-foreground/80">Hiring:</span>{" "}
            {intel.employers.slice(0, 3).map((e, i) => (
              <span key={e}>
                {i > 0 && " · "}
                <EmployerLink
                  company={e}
                  kind="company"
                  role={c.canonical_name}
                  surface="careers_explorer_card"
                  showIcon={false}
                  className="underline decoration-dotted underline-offset-2 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </span>
            ))}
          </span>
        </p>
      ) : null}

      <div className="mt-auto flex items-end justify-between gap-3 pt-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
          {salary && (
            <span className="inline-flex items-center gap-1">
              <IndianRupee className="h-3 w-3" aria-hidden="true" />
              {salary}
            </span>
          )}
          {balance != null && (
            <span className="inline-flex items-center gap-1">
              <Star className="h-3 w-3" aria-hidden="true" />
              {balanceLabel(balance)} {outOfTen(balance)}/10
            </span>
          )}
          {satisfaction != null && (
            <span className="inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3" aria-hidden="true" />
              Happy {outOfTen(satisfaction)}/10
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleOpen}
          className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-full px-3 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
        >
          Quick look <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </button>
      </div>
    </article>
  );
});


const Chip = memo(function Chip({
  children,
  tone = "plain",
}: {
  children: React.ReactNode;
  tone?: "plain" | "grow" | "demand" | "level" | "day";
}) {
  const tones: Record<string, string> = {
    plain: "border-border/60 text-foreground/80",
    grow: "border-primary/30 bg-primary/10 text-primary",
    demand: "border-border/60 bg-[hsl(var(--surface-2))] text-foreground/80",
    level: "border-transparent bg-primary/12 text-primary",
    day: "border-amber-500/30 bg-amber-500/10 text-amber-400",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  );
});

/* ------------------------------------------------------------------ */
/* Quick look panel                                                    */
/* ------------------------------------------------------------------ */

/**
 * The quick look. Radix traps focus, closes on Escape and hands focus back to
 * the card that opened it; on top of that we move focus to the role's name as
 * it opens (so a screen reader starts at the role, not the close button),
 * announce the loading state politely, and label every chart and region.
 */
function QuickLook({
  career,
  onClose,
}: {
  career: CareerContentSummary | null;
  onClose: () => void;
}) {
  const [intel, setIntel] = useState<CareerIntel | null>(null);
  const [sheet, setSheet] = useState<CareerSheetRow | null>(null);
  const [loading, setLoading] = useState(false);
  const titleRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    if (!career) return;
    let alive = true;
    setLoading(true);
    setIntel(null);
    setSheet(null);
    void loadCareerSheetRow(career.canonical_name).then((s) => {
      if (alive) setSheet(s);
    });
    void loadCareerIntel(career.canonical_name)
      .then((i) => {
        if (alive) setIntel(i);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [career]);

  // Start the reader at the role name once the panel is mounted.
  useEffect(() => {
    if (!career) return;
    const t = window.setTimeout(() => titleRef.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, [career]);

  const forecast = intel ? forecastSentence(intel) : null;
  const dayInLife = career ? getDayInLife(career.canonical_name) : undefined;
  const balance = sheet?.workLifeBalance ?? intel?.workLifeBalance ?? null;
  const satisfaction = sheet?.jobSatisfaction ?? intel?.jobSatisfaction ?? null;
  const growth = intel?.growthPct ?? sheet?.growthPct ?? null;
  const demand2026 = intel?.demand["2026"] ?? sheet?.demand2026 ?? null;
  const sheetSalary = salaryBandLpa(sheet?.salaryMinInr, sheet?.salaryMaxInr);

  return (
    <Sheet open={!!career} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-2xl"
        aria-label={career ? `${career.canonical_name} — quick look` : "Career quick look"}
      >
        {career && (
          <>
            <SheetHeader className="text-left">
              <div className="flex flex-wrap items-center gap-x-2">
                <span className="w-fit rounded-full border border-border/60 px-2.5 py-1 text-[11px] text-muted-foreground">
                  {career.career_family ?? career.career_cluster ?? "Career"}
                  {career.level ? ` · ${career.level}` : ""}
                </span>
                {(career.career_family || career.career_cluster) && (
                  <FamilyClusterHelp label="Family or cluster?" />
                )}
              </div>
              <SheetTitle
                ref={titleRef}
                tabIndex={-1}
                className="font-display text-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-lg"
              >
                {career.canonical_name}
              </SheetTitle>
              {intel?.brief && (
                <SheetDescription className="text-sm leading-relaxed">
                  {intel.brief}
                </SheetDescription>
              )}
              <p className="sr-only" role="status">
                {loading ? "Loading this career brief" : "Career brief ready. Press Escape to close."}
              </p>
            </SheetHeader>


            {loading && !intel ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
                <span className="sr-only">Loading the brief</span>
              </div>
            ) : (
              <div className="mt-6 space-y-6">
                {/* Quick stats */}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Stat
                    icon={TrendingUp}
                    label="Growth"
                    value={growth != null ? `${Math.round(growth)}%` : "—"}
                  />
                  <Stat icon={BarChart3} label="Demand 2026" value={demand2026 ?? "—"} />
                  <Stat
                    icon={Star}
                    label="Balance"
                    value={balance != null ? `${outOfTen(balance)}/10` : "—"}
                  />
                  <Stat
                    icon={Sparkles}
                    label="Satisfaction"
                    value={satisfaction != null ? `${outOfTen(satisfaction)}/10` : "—"}
                  />
                </div>


                {/* Demand outlook — animated bars, 2026 → 2040 */}
                <Section title="Market demand outlook" icon={BarChart3}>
                  <DemandChart intel={intel} />
                  {forecast && <p className="mt-3 text-xs text-muted-foreground">{forecast}</p>}
                </Section>

                {/* Future readiness meters */}
                {(intel?.futureReadiness != null ||
                  intel?.aiResilience != null ||
                  intel?.automation.exposure != null) && (
                  <Section title="How this holds up to AI" icon={ShieldCheck}>
                    <div className="space-y-3">
                      <Meter label="Future readiness" value={intel?.futureReadiness} tone="bg-emerald-400" />
                      <Meter label="AI resilience" value={intel?.aiResilience} tone="bg-sky-400" />
                      <Meter
                        label="Automation exposure"
                        value={intel?.automation.exposure}
                        tone="bg-amber-400"
                        invert
                      />
                    </div>
                    {intel?.humanAdvantage && (
                      <p className="mt-3 rounded-2xl border border-primary/20 bg-primary/[0.04] p-3 text-xs leading-relaxed text-foreground/90">
                        <span className="font-semibold text-primary">Your human edge: </span>
                        {intel.humanAdvantage}
                      </p>
                    )}
                  </Section>
                )}

                {/* A day in the life */}
                {dayInLife && (
                  <section aria-label="A day in the life">
                    <DayInLifeTimeline data={dayInLife} />
                  </section>
                )}

                {/* Salary */}
                {(intel?.salaryStart || intel?.salaryMid || sheetSalary) && (
                  <Section title="Salary bands (India)" icon={IndianRupee}>
                    <div className="grid grid-cols-2 gap-2">
                      <Detail label="Starting" value={intel?.salaryStart ?? sheetSalary} />
                      <Detail label="Mid-career" value={intel?.salaryMid} />
                    </div>
                    {intel?.salarySource && (
                      <p className="mt-2 text-[11px] text-muted-foreground">
                        Source: {intel.salarySource}
                      </p>
                    )}
                  </Section>
                )}


                {/* Career intel cards */}
                <div>
                  <div className="mb-2.5 flex items-center gap-2">
                    <Compass className="h-4 w-4 text-primary" aria-hidden="true" />
                    <h3 className="font-display text-sm font-bold uppercase tracking-wider text-foreground">
                      Career intel
                    </h3>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <IntelCard icon={Building2} label="Top employers" items={intel?.employers} accent="emerald" employerRole={career.canonical_name} />
                    <IntelCard icon={GraduationCap} label="Entry requirements" text={intel?.entry} accent="primary" />
                    <IntelCard icon={Users} label="Adjacent roles" items={intel?.adjacent} accent="violet" />
                    <IntelCard icon={ArrowRight} label="Stretch roles" items={intel?.stretch} accent="amber" />
                    <IntelCard icon={GraduationCap} label="Where people study (India)" items={intel?.pathways} accent="rose" full />
                  </div>
                </div>

                <ListSection title="What you'd actually be doing" icon={Briefcase} items={intel?.responsibilities} />
                <ListSection title="Must-have skills" icon={Sparkles} items={intel?.mustHave} />
                <ListSection title="Nice-to-have skills" icon={Star} items={intel?.niceToHave} />
                <ListSection title="Skills that hold their value" icon={TrendingUp} items={intel?.futureSkills} />

                {(intel?.remote ||
                  intel?.freelance ||
                  sheet?.remoteScore != null ||
                  balance != null ||
                  satisfaction != null) && (
                  <Section title="How the work happens" icon={Globe}>
                    <div className="space-y-3 rounded-2xl border border-border/50 bg-[hsl(var(--surface-2))] p-4">
                      <Meter
                        label={`Remote-friendly${remoteLabel(sheet?.remoteScore) ? ` · ${remoteLabel(sheet?.remoteScore)}` : ""}`}
                        value={sheet?.remoteScore}
                        tone="bg-sky-400"
                      />
                      <Meter
                        label={`Work–life balance${balanceLabel(balance) ? ` · ${balanceLabel(balance)}` : ""}`}
                        value={balance}
                        tone="bg-emerald-400"
                      />
                      <Meter label="Job satisfaction" value={satisfaction} tone="bg-primary" />
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <Detail label="Remote" value={intel?.remote ?? remoteLabel(sheet?.remoteScore)} />
                      <Detail label="Freelance" value={intel?.freelance ?? sheet?.freelance} />
                    </div>
                  </Section>
                )}


                <Link
                  to={`/careers/${encodeURIComponent(career.ci_id)}`}
                  className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-primary px-5 font-medium text-primary-foreground transition-transform hover:scale-[1.01] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  Open the full brief <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>

                <PinToVisionBoard
                  ciId={career.ci_id}
                  title={career.canonical_name}
                  family={career.career_family}
                />


                {!intel && (
                  <p className="text-xs text-muted-foreground">
                    We don't have the detailed brief for this role yet. The full page shows
                    everything the registry does hold.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ */
/* Charts                                                              */
/* ------------------------------------------------------------------ */

const YEARS = ["2026", "2030", "2035", "2040"] as const;

/**
 * Demand 2026 → 2040 as a small column chart. Heights come from the
 * registry's own bands via `demandLevel` — nothing is invented, and a year
 * we have no band for renders as an empty column rather than a zero.
 *
 * Memoised, and the bands are derived once per intel object: animating four
 * columns is cheap, recomputing them on every parent render is not. With
 * reduced motion the columns simply appear at their final height.
 */
const DemandChart = memo(function DemandChart({ intel }: { intel: CareerIntel | null }) {
  const reduceMotion = useReducedMotion() ?? false;
  const columns = useMemo(
    () =>
      YEARS.map((yr) => {
        const band = intel?.demand[yr] ?? null;
        return { yr, band, level: demandLevel(band), tone: demandTone(band) };
      }),
    [intel],
  );

  const summary = columns
    .map((col) => `${col.yr}: ${col.band ?? "no data"}`)
    .join(", ");

  return (
    <div className="rounded-2xl border border-border/50 bg-[hsl(var(--surface-2))] p-4">
      <div
        className="flex h-32 items-end gap-3"
        role="img"
        aria-label={`Market demand outlook — ${summary}`}
      >
        {columns.map((col, i) => (
          <div key={col.yr} className="flex min-w-0 flex-1 flex-col items-center justify-end gap-2">
            <span className="truncate text-[10px] font-semibold text-foreground/80">
              {col.band || "—"}
            </span>
            <div className="relative flex h-20 w-full items-end overflow-hidden rounded-xl bg-muted/40">
              <motion.span
                className={`block w-full rounded-xl ${col.tone}`}
                initial={reduceMotion ? false : { height: 0 }}
                animate={{ height: `${col.level ?? 0}%` }}
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : { duration: 0.6, delay: 0.08 * i, ease: [0.22, 1, 0.36, 1] }
                }
              />
            </div>
            <span className="text-[11px] tabular-nums text-muted-foreground" aria-hidden="true">
              {col.yr}
            </span>
          </div>
        ))}
      </div>
      {intel?.demandBasis && (
        <p className="mt-3 text-[11px] text-muted-foreground">Basis: {intel.demandBasis}</p>
      )}
    </div>
  );
});

/** A 0–10 or 0–100 signal as an animated bar. Missing stays missing. */
const Meter = memo(function Meter({
  label,
  value,
  tone,
  invert = false,
}: {
  label: string;
  value?: number | null;
  tone: string;
  invert?: boolean;
}) {
  const reduceMotion = useReducedMotion() ?? false;
  if (value == null) return null;
  const pct = Math.max(0, Math.min(100, value <= 10 ? value * 10 : value));
  const readout = `${value <= 10 ? `${value} out of 10` : `${Math.round(value)} percent`}${invert ? " exposed" : ""}`;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold tabular-nums text-foreground" aria-hidden="true">
          {value <= 10 ? `${value}/10` : `${Math.round(value)}%`}
          {invert ? " exposed" : ""}
        </span>
      </div>
      <div
        className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted/40"
        role="meter"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label}: ${readout}`}
      >
        <motion.span
          className={`block h-full rounded-full ${tone}`}
          initial={reduceMotion ? false : { width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={reduceMotion ? { duration: 0 } : { duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </div>
  );
});



const Stat = memo(function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-border/50 bg-[hsl(var(--surface-2))] p-3">
      <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </p>
      <p className="mt-1 font-display text-sm font-bold text-foreground">{value}</p>
    </div>
  );
});

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h4 className="mb-2 flex items-center gap-2 font-display text-sm font-semibold text-foreground">
        <Icon className="h-4 w-4 text-primary" /> {title}
      </h4>
      {children}
    </section>
  );
}

function Detail({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="rounded-2xl border border-border/50 bg-[hsl(var(--surface-2))] p-3">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-foreground">{value || "—"}</p>
    </div>
  );
}

function ListSection({
  title,
  icon,
  items,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items?: string[];
}) {
  if (!items || items.length === 0) return null;
  return (
    <Section title={title} icon={icon}>
      <ul className="space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground [list-style-type:disc]">
        {items.map((it, i) => (
          <li key={`${it}-${i}`}>{it}</li>
        ))}
      </ul>
    </Section>
  );
}

const ACCENTS: Record<string, string> = {
  emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-400",
  primary: "border-primary/30 bg-primary/5 text-primary",
  amber: "border-amber-500/30 bg-amber-500/5 text-amber-400",
  violet: "border-violet-500/30 bg-violet-500/5 text-violet-400",
  rose: "border-rose-500/30 bg-rose-500/5 text-rose-400",
};

const IntelCard = memo(function IntelCard({
  icon: Icon,
  label,
  text,
  items,
  accent = "primary",
  full = false,
  employerRole,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  text?: string | null;
  items?: string[];
  accent?: string;
  full?: boolean;
  /** When set, each chip becomes a LinkedIn employer link for this career. */
  employerRole?: string;
}) {
  const chips = items?.filter(Boolean) ?? [];
  if (!chips.length && !text?.trim()) return null;
  const tone = ACCENTS[accent] ?? ACCENTS.primary;
  return (
    <div className={`rounded-2xl border border-border/50 bg-card/60 p-4 ${full ? "sm:col-span-2" : ""}`}>
      <div className="mb-2.5 flex items-center gap-2">
        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-xl border ${tone}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
      </div>
      {chips.length ? (
        <div className="flex flex-wrap gap-1.5">
          {chips.slice(0, 12).map((it, i) =>
            employerRole !== undefined ? (
              <EmployerLink
                key={`${it}-${i}`}
                company={it}
                kind="jobs"
                role={employerRole}
                surface="careers_explorer_intel"
              />
            ) : (
              <span
                key={`${it}-${i}`}
                className="rounded-lg border border-border/60 bg-background/60 px-2 py-1 text-xs text-foreground/90"
              >
                {it}
              </span>
            ),
          )}
        </div>
      ) : (

        <p className="text-sm leading-relaxed text-foreground/90">{text}</p>
      )}
    </div>
  );
});
